import { createSlice } from '@reduxjs/toolkit';

import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';

import axios, { endpoints } from '../../utils/axios';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  templates: [],
  presets: [],
  publicTemplates: [],
  currentTemplate: null,
  publicPagination: { page: 1, limit: 20, total: 0 },
};

const slice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    startLoading (state) {
      state.isLoading = true;
    },
    endLoading (state) {
      state.isLoading = false;
    },
    hasError (state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    resetError (state) {
      state.isLoading = false;
      state.error = null;
    },
    getTemplatesSuccess (state, action) {
      state.templates = action.payload.userTemplates;
      state.presets = action.payload.presets;
      state.isLoading = false;
    },
    getPublicTemplatesSuccess (state, action) {
      state.publicTemplates = action.payload.templates;
      state.publicPagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
      };
      state.isLoading = false;
    },
    getTemplateSuccess (state, action) {
      state.currentTemplate = action.payload;
      state.isLoading = false;
    },
    clearCurrentTemplate (state) {
      state.currentTemplate = null;
    },
    createTemplateSuccess (state, action) {
      state.templates = [action.payload, ...state.templates];
      state.isLoading = false;
    },
    updateTemplateSuccess (state, action) {
      const index = state.templates.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.templates[index] = action.payload;
      }
      state.isLoading = false;
    },
    deleteTemplateSuccess (state, action) {
      state.templates = state.templates.filter((t) => t.id !== action.payload);
      state.isLoading = false;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { clearCurrentTemplate, resetError } = slice.actions;

// ----------------------------------------------------------------------

export function getTemplates () {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(endpoints.template.list);
      dispatch(slice.actions.getTemplatesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
    }
  };
}

// ----------------------------------------------------------------------

export function getPublicTemplates ({ page = 1, limit = 20, category, search } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', limit);
      if (category && category !== 'all') params.set('category', category);
      if (search) params.set('search', search);

      const response = await axios.get(`${endpoints.template.public}?${params.toString()}`);
      dispatch(slice.actions.getPublicTemplatesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
    }
  };
}

// ----------------------------------------------------------------------

export function getTemplate (id) {
  return async (dispatch) => {
    dispatch(slice.actions.resetError());
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(endpoints.template.details(id));
      dispatch(slice.actions.getTemplateSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
    }
  };
}

// ----------------------------------------------------------------------

export function createTemplate (data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(endpoints.template.list, data);
      dispatch(slice.actions.createTemplateSuccess(response.data));
      trackEvent(ANALYTICS_EVENTS.TEMPLATE_CREATE_COMPLETED, { category: data?.category });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateTemplate (id, data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put(endpoints.template.details(id), data);
      dispatch(slice.actions.updateTemplateSuccess(response.data));
      trackEvent(ANALYTICS_EVENTS.TEMPLATE_UPDATE_COMPLETED, { category: data?.category });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function deleteTemplate (id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete(endpoints.template.details(id));
      dispatch(slice.actions.deleteTemplateSuccess(id));
      trackEvent(ANALYTICS_EVENTS.TEMPLATE_DELETE_COMPLETED);
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function applyTemplate (id) {
  return async () => {
    const response = await axios.post(endpoints.template.apply(id));
    return response.data;
  };
}

// ----------------------------------------------------------------------

export function duplicateTemplate (id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(endpoints.template.duplicate(id));
      dispatch(slice.actions.createTemplateSuccess(response.data));
      trackEvent(ANALYTICS_EVENTS.TEMPLATE_DUPLICATE_COMPLETED, { templateId: id });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw error;
    }
  };
}
